import React from 'react'
import FormErrorMessage from '../../../../components/FormErrorMessage/FormErrorMessage'
import {FormFieldType} from '../utils/utils'
import ClearableFieldWrapper from '../../../../components/ClearableFieldWrapper/ClearableFieldWrapper'

const FIELD_CHARACTER_LIMITS: Record<string, number> = {
  venueName: 100,
  addressLine1: 100,
  addressLine2: 100,
  city: 100,
  postalCode: 25,
  additionalNotes: 150,
  contactName: 100,
  contactEmail: 100,
}

const getFieldCharacterLimit = (formKey: string) => FIELD_CHARACTER_LIMITS[formKey] ?? 200

type FormGroupProps = {
  formField: FormFieldType
  handleFormFieldOnChange: (formKey: string, value: string) => void
  getInputValue: (formKey: string) => any
  autoFocus?: boolean
}

export default function FormGroup(props: FormGroupProps) {
  const {formField, handleFormFieldOnChange, getInputValue, autoFocus} = props
  const {label, placeholder, required, id, errorMessage, formKey} = formField

  const inputClasses = React.useMemo(() => {
    return [
      'border text-sm rounded w-full p-3',
      errorMessage ? 'focus:border-red-500 dark:focus:border-red-400/90' : 'focus:border-sky-500 dark:focus:border-sky-400',
      'text-gray-700 dark:text-slate-200 leading-none focus:outline-none dark:bg-slate-900/60',
      errorMessage
        ? 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-950/30'
        : 'border-gray-300 dark:border-slate-600',
    ].join(' ')
  }, [errorMessage])

  const rawValue = getInputValue(formKey)
  const value = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '')
  const maxLength = getFieldCharacterLimit(formKey)
  const isAtLimit = value.length === maxLength
  const showClear = value.length > 0

  const validatedErrorMessage =
    typeof errorMessage === 'string' && errorMessage.length > 0 ? errorMessage : null

  const hasError = Boolean(validatedErrorMessage)

  const handleClear = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      handleFormFieldOnChange(formKey, '')
    },
    [handleFormFieldOnChange, formKey]
  )

  const renderedInput = React.useMemo(() => {
    const commonInputProps = {
      placeholder,
      required,
      id,
      name: id,
      value,
      maxLength,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        handleFormFieldOnChange(formKey, event.target.value),
    }

    if (formField.formKey === 'additionalNotes') {
      return (
        <>
          <ClearableFieldWrapper
            showClear={showClear}
            hasError={hasError}
            onClear={handleClear}
            label={label}
            variant="top-right"
          >
            <textarea
              {...commonInputProps}
              className={[
                'border text-sm  rounded w-full focus:border-sky-500 dark:focus:border-sky-400',
                'text-gray-700 dark:text-slate-200 leading-[1.3] focus:outline-none dark:bg-slate-900/60',
                'border-gray-300 dark:border-slate-600',
                'h-[100px] resize-none py-2 pl-2 pr-8',
              ].join(' ')}
            />
          </ClearableFieldWrapper>
          <div
            className={[
              'text-xs text-gray-500 dark:text-slate-400 mt-1 flex justify-end',
              isAtLimit ? 'font-bold' : '',
            ].join(' ')}
          >
            <p>
              {value.length}/{maxLength}
            </p>
          </div>
        </>
      )
    }

    if (formField.formKey === 'contactEmail') {
      return (
        <ClearableFieldWrapper
          showClear={showClear}
          hasError={hasError}
          onClear={handleClear}
          label={label}
          variant="middle-right"
        >
          <input
            {...commonInputProps}
            className={`${inputClasses} pr-9`}
            type="email"
            autoFocus={!!autoFocus}
          />
        </ClearableFieldWrapper>
      )
    }

    return (
      <ClearableFieldWrapper
        showClear={showClear}
        hasError={hasError}
        onClear={handleClear}
        label={label}
        variant="middle-right"
      >
        <input
          {...commonInputProps}
          className={`${inputClasses} pr-9`}
          type="text"
          autoFocus={!!autoFocus}
        />
      </ClearableFieldWrapper>
    )
  }, [
    formField.formKey,
    placeholder,
    required,
    id,
    value,
    maxLength,
    handleFormFieldOnChange,
    formKey,
    inputClasses,
    isAtLimit,
    showClear,
    hasError,
    handleClear,
    label,
    autoFocus,
  ])

  return (
    <div className="flex flex-col gap-1 mb-4">
      <label
        className={`block ${hasError ? 'text-red-600 dark:text-red-300' : 'text-sky-500 dark:text-sky-400'} text-xs`}
        htmlFor={id}
      >
        {label}
        <span className="text-red-600 pl-1">{required ? '*' : ''}</span>
      </label>
      {renderedInput}
      {validatedErrorMessage && <FormErrorMessage message={validatedErrorMessage} />}
    </div>
  )
}
